import React, { createContext, useContext, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';
import localStorageService from '../utils/localStorageService';

const AppDataContext = createContext();

export const useAppData = () => {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error('useAppData must be used within an AppDataProvider');
  }
  return context;
};

export const AppDataProvider = ({ children }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // Global states
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Dashboard stats
  const [globalStats, setGlobalStats] = useState({
    totalMembers: 0,
    activeMembers: 0,
    totalRevenue: 0,
    equipmentStatus: "0/0",
    todayAttendance: 0,
    lowStockItems: 0
  });

  // Fetch dashboard overview data from localStorage
  const { data: dashboardData } = useQuery(
    'dashboardOverview',
    () => {
      try {
        // Get data from localStorage to calculate dashboard stats
        const members = localStorageService.getAllItems('members') || [];
        const payments = localStorageService.getAllItems('payments') || [];
        const equipment = localStorageService.getAllItems('equipment') || [];
        const sessions = localStorageService.getAllItems('training-sessions') || [];
        
        // Calculate stats from real data
        const activeMembers = members.filter(m => m.status === 'active').length;
        const thisMonth = new Date().getMonth() + 1;
        const thisYear = new Date().getFullYear();
        const monthlyRevenue = payments
          .filter(p => {
            const paymentDate = new Date(p.createdAt || p.date);
            return paymentDate.getMonth() + 1 === thisMonth && paymentDate.getFullYear() === thisYear;
          })
          .reduce((total, p) => total + (p.totalAmount || p.paidAmount || p.amount || 0), 0);
        
        const availableEquipment = equipment.filter(e => e.status === 'available').length;
        
        return {
          members: { total: members.length, active: activeMembers },
          revenue: { thisMonth: monthlyRevenue },
          equipment: { available: availableEquipment, total: equipment.length },
          attendance: { todayVisits: sessions.length }
        };
      } catch (error) {
        console.error('Error calculating dashboard data:', error);
        return { members: { total: 0, active: 0 }, revenue: { thisMonth: 0 }, equipment: { available: 0, total: 0 }, attendance: { todayVisits: 0 } };
      }
    },
    {
      onSuccess: (d) => {
        setGlobalStats({
          totalMembers: d.members?.total || 0,
          activeMembers: d.members?.active || 0,
          totalRevenue: d.revenue?.thisMonth || 0,
          equipmentStatus: `${d.equipment?.available || 0}/${d.equipment?.total || 0}`,
          todayAttendance: d.attendance?.todayVisits || 0,
          lowStockItems: 0
        });
      },
      onError: (error) => {
        console.error('Error calculating dashboard data:', error);
      },
      refetchOnWindowFocus: false,
      staleTime: 1 * 60 * 1000 // 1 minute
    }
  );

  // Fetch notifications from localStorage
  const { data: notificationsData } = useQuery(
    'notifications',
    () => {
      try {
        const notifications = JSON.parse(localStorage.getItem('notifications') || '[]');
        const unreadCount = notifications.filter(n => !n.read).length;
        return { notifications, unreadCount };
      } catch (error) {
        console.error('Error fetching notifications:', error);
        return { notifications: [], unreadCount: 0 };
      }
    },
    {
      onSuccess: (response) => {
        const unread = response?.unreadCount ?? 0;
        setUnreadCount(unread);
      },
      onError: (error) => {
        console.error('Error fetching notifications:', error);
      },
      refetchOnWindowFocus: false,
      staleTime: 1 * 60 * 1000 // 1 minute
    }
  );

  // Members queries from localStorage
  const membersQuery = useQuery(
    'members',
    () => {
      try {
        const members = localStorageService.getAllItems('members');
        return { data: { members } };
      } catch (error) {
        console.error('Error fetching members:', error);
        return { data: { members: [] } };
      }
    },
    {
      onError: (error) => {
        console.error('Error fetching members:', error);
      },
      staleTime: 1 * 60 * 1000 // 1 minute
    }
  );

  // Subscriptions queries from localStorage
  const subscriptionsQuery = useQuery(
    'subscriptions',
    () => {
      try {
        const subscriptions = localStorageService.getAllItems('subscriptions') || [];
        const plans = localStorageService.getAllItems('subscription-plans') || [];
        return { data: { subscriptions, plans } };
      } catch (error) {
        console.error('Error fetching subscriptions:', error);
        return { data: { subscriptions: [], plans: [] } };
      }
    },
    {
      onError: (error) => {
        console.error('Error fetching subscriptions:', error);
      },
      staleTime: 1 * 60 * 1000 // 1 minute
    }
  );

  // Trainers queries from localStorage
  const trainersQuery = useQuery(
    'trainers',
    () => {
      try {
        const trainers = localStorageService.getAllItems('trainers');
        return { data: { trainers } };
      } catch (error) {
        console.error('Error fetching trainers:', error);
        return { data: { trainers: [] } };
      }
    },
    {
      onError: (error) => {
        console.error('Error fetching trainers:', error);
      },
      staleTime: 1 * 60 * 1000 // 1 minute
    }
  );

  // Equipment queries from localStorage
  const equipmentQuery = useQuery(
    'equipment',
    () => {
      try {
        const equipment = localStorageService.getAllItems('equipment') || [];
        return { data: { equipment } };
      } catch (error) {
        console.error('Error fetching equipment:', error);
        return { data: { equipment: [] } };
      }
    },
    {
      onError: (error) => {
        console.error('Error fetching equipment:', error);
      },
      staleTime: 1 * 60 * 1000 // 1 minute
    }
  );

  // Sessions queries from localStorage
  const todaySessionsQuery = useQuery(
    'todaySessions',
    () => {
      try {
        const sessions = localStorageService.getAllItems('training-sessions') || [];
        const today = new Date().toISOString().split('T')[0];
        const todaySessions = sessions.filter(session => {
          const sessionDate = session.date || session.startDate;
          return sessionDate && sessionDate.split('T')[0] === today;
        });
        return { data: { todaySessions, totalAttendance: todaySessions.length } };
      } catch (error) {
        console.error('Error fetching today sessions:', error);
        return { data: { todaySessions: [], totalAttendance: 0 } };
      }
    },
    {
      onError: (error) => {
        console.error('Error fetching today sessions:', error);
      },
      staleTime: 1 * 60 * 1000 // 1 minute
    }
  );

  // Add notification (stub - notifications handled by backend)
  const addNotification = (notification) => {
    // Just return an ID for compatibility
    return Date.now();
  };

  // Mark notification as read (stub - notifications handled by backend)
  const markAsRead = (notificationId) => {
    // Function stub
  };

  // Get equipment needing maintenance
  const getMaintenanceNeeded = () => {
    const equipment = equipmentQuery.data?.data?.equipment || [];
    return equipment.filter(item => 
      item.status === 'maintenance' || 
      (item.nextMaintenanceDate && new Date(item.nextMaintenanceDate) <= new Date())
    );
  };

  // Update global stats
  const updateGlobalStats = (newStats) => {
    setGlobalStats(prev => ({
      ...prev,
      ...newStats
    }));
  };

  // Expose the context
  const value = {
    // Data states
    selectedMember,
    setSelectedMember,
    selectedTrainer,
    setSelectedTrainer,
    selectedEquipment,
    setSelectedEquipment,
    notifications: notificationsData?.notifications || [],
    unreadCount,
    globalStats,
    
    // Data access functions
    getMembers: () => membersQuery.data?.data?.members || [],
    getMemberById: (id) => {
      const members = membersQuery.data?.data?.members || [];
      return members.find(member => member.id === id);
    },
    
    getTrainers: () => trainersQuery.data?.data?.trainers || [],
    getTrainerById: (id) => {
      const trainers = trainersQuery.data?.data?.trainers || [];
      return trainers.find(trainer => trainer.id === id);
    },
    
    getEquipment: () => equipmentQuery.data?.data?.equipment || [],
    getEquipmentById: (id) => {
      const equipment = equipmentQuery.data?.data?.equipment || [];
      return equipment.find(item => item.id === id);
    },
    
    getSubscriptionPlans: () => subscriptionsQuery.data?.data?.plans || [],
    getSubscriptionPlanById: (id) => {
      const plans = subscriptionsQuery.data?.data?.plans || [];
      return plans.find(plan => plan.id === id);
    },
    
    getSubscriptions: () => subscriptionsQuery.data?.data?.subscriptions || [],
    getSubscriptionById: (id) => {
      const subscriptions = subscriptionsQuery.data?.data?.subscriptions || [];
      return subscriptions.find(sub => sub.id === id);
    },
    
    getActiveSubscriptions: () => {
      const subscriptions = subscriptionsQuery.data?.data?.subscriptions || [];
      return subscriptions.filter(sub => sub.isActive);
    },
    
    getExpiringSubscriptions: () => {
      const subscriptions = subscriptionsQuery.data?.data?.subscriptions || [];
      const nextWeek = new Date();
      nextWeek.setDate(nextWeek.getDate() + 7);
      
      return subscriptions.filter(sub => {
        const endDate = new Date(sub.endDate);
        return sub.isActive && endDate <= nextWeek && endDate >= new Date();
      });
    },
    
    calculateTotalRevenue: () => {
      const subscriptions = subscriptionsQuery.data?.data?.subscriptions || [];
      return subscriptions
        .filter(sub => sub.isActive)
        .reduce((total, sub) => total + (sub.price || 0), 0);
    },
    
    // Utility functions
    addNotification,
    markAsRead,
    getMaintenanceNeeded,
    updateGlobalStats,
    
    // Loading states
    isLoading: {
      members: membersQuery.isLoading,
      subscriptions: subscriptionsQuery.isLoading,
      trainers: trainersQuery.isLoading,
      equipment: equipmentQuery.isLoading,
      todaySessions: todaySessionsQuery.isLoading,
      dashboard: dashboardData?.isLoading
    }
  };

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
};

export default AppDataContext; 